import React from 'react';
import { motion } from 'framer-motion';
import { Volume2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { EditableText, EditableSection, useAdmin } from './AdminContentEditor';

export const DataDreamerDefinition = () => {
  const { theme } = useTheme();
  const { isEditMode } = useAdmin();
  
  const content = (
    <section className="py-16 md:py-24 px-4 md:px-6 relative" aria-labelledby="definition-heading">
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* High contrast container for accessibility */}
          <div 
            className="relative rounded-[24px] md:rounded-[40px] p-6 md:p-8 lg:p-12 backdrop-blur-xl overflow-hidden border"
            style={{
              background: 'rgba(0, 0, 0, 0.65)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
            }}
            role="article"
          >
            {/* Decorative puffs */}
            <div 
              className="absolute -top-10 -left-10 w-32 h-32 rounded-full blur-2xl"
              style={{ background: theme === 'sunrise' ? 'rgba(251,191,36,0.15)' : 'rgba(74,222,128,0.1)' }}
              aria-hidden="true"
            />
            <div 
              className="absolute -bottom-8 -right-8 w-28 h-28 rounded-full blur-2xl"
              style={{ background: theme === 'sunrise' ? 'rgba(244,114,182,0.15)' : 'rgba(45,212,191,0.1)' }}
              aria-hidden="true"
            />
            
            {/* Content */}
            <div className="relative z-10">
              {/* Dictionary header */}
              <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-6 flex-wrap">
                <motion.h2 
                  id="definition-heading"
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg"
                >
                  <EditableText
                    sectionId="definition"
                    field="title"
                    defaultValue="data dreamer"
                    className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg"
                  />
                </motion.h2>
                
                {/* Pronunciation with speaker icon */}
                <div className="flex items-center gap-2">
                  <button 
                    className="p-2 rounded-full transition-colors hover:bg-white/20 text-white/90 focus:outline-none focus:ring-2 focus:ring-white/50"
                    aria-label="Listen to pronunciation"
                  >
                    <Volume2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <EditableText
                    sectionId="definition"
                    field="pronunciation"
                    defaultValue="/ˈdeɪtə ˈdriːmər/"
                    className="text-sm font-mono text-white/80"
                    as="span"
                  />
                </div>
              </div>
              
              {/* Part of speech */}
              <p className="text-sm mb-4 text-white/80 italic">
                noun
              </p>
              
              {/* Definition - fits on 3 lines on mobile */}
              <motion.div
                className="text-sm sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-light text-white"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <EditableText
                  sectionId="definition"
                  field="description"
                  defaultValue="one who thinks in both ideas and systems; sees the big picture and the small details; creates with passion and builds with purpose."
                  className="text-sm sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-light text-white"
                  multiline={true}
                />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );

  if (isEditMode) {
    return <EditableSection sectionId="definition">{content}</EditableSection>;
  }

  return content;
};

export default DataDreamerDefinition;
